import { Button, ButtonBase, Stack, Typography } from '@mui/material';
import { TooltipContentType } from 'enums';
import { GrammarReplacement, Paraphrase, SuggestionItem } from 'features/augmented-writing/augmented-writing.types';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';
import GrammarContent from './grammar-content';
import ParaphraseContent from './paraphrase-content';

interface Props {
  suggestion: SuggestionItem;
  active: boolean;
  disableBorder?: boolean;
  /** 'default' keeps the legacy card, 'panel' renders the two-zone panel design */
  variant?: 'default' | 'panel';

  skipSuggestion(content: any, id: string): () => void;

  ignoreSuggestion(content: any, id: string): () => void;

  insertAtPosition(word: string, pos: number | number[]): () => void;

  activateSuggestionCard?(highlightId: string): () => void;
}

const SuggestionCard: React.FC<Props> = ({
  suggestion,
  active,
  disableBorder,
  variant = 'default',
  skipSuggestion,
  ignoreSuggestion,
  insertAtPosition,
  activateSuggestionCard,
}) => {
  const { classes, cx } = useStyles({ disableBorder });
  const { t } = useTranslation();

  const isGrammar = suggestion.contentType === TooltipContentType.GRAMMAR_REPLACEMENT;
  const title = isGrammar ? t('grammarOptimization') : t('paiMatchOptimization');
  const activate = !_.isNil(activateSuggestionCard) ? activateSuggestionCard(suggestion.highlightId) : undefined;

  if (variant === 'panel') {
    const content = suggestion.content;
    const options = content.suggestions || [];
    const singleOption = options.length === 1;

    return (
      <div
        className={cx(classes.panelCard, active ? 'active' : null)}
        id={`card-${suggestion.highlightId}`}
        onClick={activate}
        role={'presentation'}
      >
        <div className={classes.panelHeader}>
          <span className={cx(classes.panelDot, isGrammar ? 'grammar' : null)} />
          <Typography className={classes.panelKind}>{title}</Typography>
        </div>
        {isGrammar ? (
          <GrammarContent
            grammarReplacement={content as GrammarReplacement}
            active={active}
            variant={'panel'}
            hideOptions={singleOption}
            insertAtPosition={insertAtPosition}
          />
        ) : (
          <ParaphraseContent
            paraphrase={content as Paraphrase}
            active={active}
            variant={'panel'}
            hideOptions={singleOption}
            insertAtPosition={insertAtPosition}
          />
        )}
        {active ? (
          <Stack className={classes.panelActions} direction={'row'} gap={'8px'} flexWrap={'wrap'}>
            {singleOption ? (
              <Button className={classes.applyButton} onClick={insertAtPosition(options[0].content, content.highlightPosition)}>
                {t('applySuggestion')}
              </Button>
            ) : null}
            <Button className={classes.ghostButton} onClick={skipSuggestion(content, suggestion.highlightId)}>
              {t('suggestionIgnoreOnce')}
            </Button>
            <Button className={classes.ghostButton} onClick={ignoreSuggestion(content, suggestion.highlightId)}>
              {t('suggestionIgnoreAlways')}
            </Button>
          </Stack>
        ) : null}
      </div>
    );
  }

  return (
    <ButtonBase className={cx(classes.suggestionCard, !active ? 'not-active' : null)} id={`card-${suggestion.highlightId}`} onClick={activate}>
      <Typography className={classes.title}>{title}</Typography>
      <div className={classes.content}>
        {isGrammar ? (
          <GrammarContent grammarReplacement={suggestion.content as GrammarReplacement} active={active} insertAtPosition={insertAtPosition} />
        ) : (
          <ParaphraseContent paraphrase={suggestion.content as Paraphrase} active={active} insertAtPosition={insertAtPosition} />
        )}
      </div>
      {active ? (
        <Stack className={classes.actions} direction={'row'} justifyContent={'center'} alignItems={'center'} gap={2}>
          <PaiLink text={t('ignoreOnce')} size={'xs'} underline={'always'} action={skipSuggestion(suggestion.content, suggestion.highlightId)} />
          <PaiLink text={t('alwaysIgnore')} size={'xs'} underline={'always'} action={ignoreSuggestion(suggestion.content, suggestion.highlightId)} />
        </Stack>
      ) : null}
    </ButtonBase>
  );
};

const useStyles = makeStyles<{ disableBorder?: boolean }>()((theme, { disableBorder }) => ({
  suggestionCard: {
    width: '100%',
    display: 'block',
    textAlign: 'start',
    background: '#FFF',
    boxShadow: disableBorder ? undefined : Shadows.GREY_SHADOW_LIGHT,
    '&.not-active': {
      cursor: 'pointer',
      opacity: 0.5,
      '&:hover': {
        opacity: 1,
      },
    },
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  title: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    textAlign: 'center',
    paddingTop: '8px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    padding: '4px',
    borderTop: `1px solid ${theme.palette.greys.lighter}`,
  },
  content: {
    padding: '8px 16px',
  },
  panelCard: {
    width: '100%',
    background: '#FFF',
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: '12px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    textAlign: 'start',
    cursor: 'pointer',
    '&:hover': {
      borderColor: theme.palette.primary.light,
    },
    '&.active': {
      borderColor: theme.palette.primary.main,
      background: '#FAFAFF',
      cursor: 'default',
    },
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  panelDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
    background: theme.palette.primary.main,
    '&.grammar': {
      background: theme.palette.analysisSecondary.main,
    },
  },
  panelKind: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
    color: theme.palette.greys.dark,
  },
  panelActions: {
    marginTop: '2px',
  },
  applyButton: {
    height: '34px',
    padding: '0 14px',
    minWidth: 'unset',
    borderRadius: '8px',
    background: theme.palette.primary.main,
    color: '#FFF',
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
    textTransform: 'none',
    '&:hover': {
      background: theme.palette.primary.dark,
    },
  },
  ghostButton: {
    height: '34px',
    padding: '0 12px',
    minWidth: 'unset',
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: '8px',
    background: '#FFF',
    color: theme.palette.greys.dark,
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 400,
    textTransform: 'none',
    '&:hover': {
      background: '#FFF',
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
  },
}));

export default SuggestionCard;
